/**
 * Client service for Sector Operations Phone Access (R39 A14.a, SO-D24, SO-D298).
 */

import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { WorkspaceRole } from '../types/workspace'
import {
  resolveOpsPhoneItems,
  type OpsPhoneItemKey,
  type OpsPhoneAccessDoc,
  type OpsPhoneAccessPersonDoc,
} from '../types/ops'

export interface SetOpsPhoneRoleAccessInput {
  workspaceId: string
  role: WorkspaceRole
  items: OpsPhoneItemKey[]
}

export interface SetOpsPhoneRoleAccessResult {
  success: boolean
  roleAllow: Partial<Record<WorkspaceRole, OpsPhoneItemKey[]>>
}

export interface ApplyOpsPhoneRecommendedSetInput {
  workspaceId: string
  role: WorkspaceRole
}

export interface ApplyOpsPhoneRecommendedSetResult {
  success: boolean
  role: WorkspaceRole
  items: OpsPhoneItemKey[]
}

export interface SetOpsPhonePersonAccessInput {
  workspaceId: string
  uid: string
  allow: OpsPhoneItemKey[]
  deny: OpsPhoneItemKey[]
}

export interface SetOpsPhonePersonAccessResult {
  success: boolean
  uid: string
  allow: OpsPhoneItemKey[]
  deny: OpsPhoneItemKey[]
}

export interface GetMyOpsPhoneItemsInput {
  workspaceId: string
}

export interface GetMyOpsPhoneItemsResult {
  items: OpsPhoneItemKey[]
}

export async function setOpsPhoneRoleAccess(
  input: SetOpsPhoneRoleAccessInput,
): Promise<SetOpsPhoneRoleAccessResult> {
  return callable<SetOpsPhoneRoleAccessInput, SetOpsPhoneRoleAccessResult>('setOpsPhoneRoleAccess', input)
}

export async function applyOpsPhoneRecommendedSet(
  input: ApplyOpsPhoneRecommendedSetInput,
): Promise<ApplyOpsPhoneRecommendedSetResult> {
  return callable<ApplyOpsPhoneRecommendedSetInput, ApplyOpsPhoneRecommendedSetResult>('applyOpsPhoneRecommendedSet', input)
}

export async function setOpsPhonePersonAccess(
  input: SetOpsPhonePersonAccessInput,
): Promise<SetOpsPhonePersonAccessResult> {
  return callable<SetOpsPhonePersonAccessInput, SetOpsPhonePersonAccessResult>('setOpsPhonePersonAccess', input)
}

export async function getMyOpsPhoneItems(
  input: GetMyOpsPhoneItemsInput,
): Promise<GetMyOpsPhoneItemsResult> {
  return callable<GetMyOpsPhoneItemsInput, GetMyOpsPhoneItemsResult>('getMyOpsPhoneItems', input)
}

export function subscribeToOpsPhoneAccess(
  workspaceId: string,
  callback: (access: OpsPhoneAccessDoc | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback(null)
    return () => undefined
  }

  try {
    const accessRef = doc(firestoreDb, 'ops_phone_access', workspaceId)
    return onSnapshot(
      accessRef,
      (snap) => {
        if (!snap.exists()) {
          callback(null)
          return
        }
        callback(snap.data() as OpsPhoneAccessDoc)
      },
      (err) => {
        onError?.(err instanceof Error ? err : new Error(String(err)))
      },
    )
  } catch (err) {
    onError?.(err instanceof Error ? err : new Error(String(err)))
    return () => undefined
  }
}

export function subscribeToWorkspacePhonePeopleOverrides(
  workspaceId: string,
  callback: (people: OpsPhoneAccessPersonDoc[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const q = query(
    collection(firestoreDb, 'ops_phone_access_people'),
    where('workspaceId', '==', workspaceId),
  )
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => d.data() as OpsPhoneAccessPersonDoc)
      callback(list)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export function subscribeToOpsPhonePersonAccess(
  workspaceId: string,
  uid: string,
  callback: (person: OpsPhoneAccessPersonDoc | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !uid) {
    callback(null)
    return () => undefined
  }

  try {
    const personRef = doc(firestoreDb, 'ops_phone_access_people', `${workspaceId}_${uid}`)
    return onSnapshot(
      personRef,
      (snap) => {
        if (!snap.exists()) {
          callback(null)
          return
        }
        callback(snap.data() as OpsPhoneAccessPersonDoc)
      },
      (err) => {
        onError?.(err instanceof Error ? err : new Error(String(err)))
      },
    )
  } catch (err) {
    onError?.(err instanceof Error ? err : new Error(String(err)))
    return () => undefined
  }
}

export function subscribeToMyOpsPhoneItems(
  workspaceId: string,
  uid: string,
  role: WorkspaceRole,
  modules: Record<string, boolean | undefined> | null | undefined,
  callback: (items: OpsPhoneItemKey[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !uid) {
    callback([])
    return () => undefined
  }

  let latestAccess: OpsPhoneAccessDoc | null = null
  let latestPerson: OpsPhoneAccessPersonDoc | null = null

  const recompute = () => {
    const items = resolveOpsPhoneItems({
      role,
      roleAllow: latestAccess?.roleAllow,
      person: latestPerson,
      modules,
    })
    callback(items)
  }

  const unsubAccess = subscribeToOpsPhoneAccess(
    workspaceId,
    (access) => {
      latestAccess = access
      recompute()
    },
    onError,
  )

  const unsubPerson = subscribeToOpsPhonePersonAccess(
    workspaceId,
    uid,
    (person) => {
      latestPerson = person
      recompute()
    },
    onError,
  )

  return () => {
    unsubAccess()
    unsubPerson()
  }
}
